import React, { useMemo } from "react";
import { X, Users, Building2, Briefcase, UserPlus, Crown } from "lucide-react";

export default function StatsModal({ nodes, onClose }) {
  const stats = useMemo(() => {
    const people = nodes.filter((n) => n.node_type === "person");
    const boardNodes = nodes.filter((n) => n.node_type === "board" || n.node_type === "delegate");
    const cSuiteNodes = nodes.filter((n) => n.node_type === "c_suite");
    const divisions = nodes.filter((n) => n.node_type === "division");
    const vacancies = nodes.filter((n) => n.is_vacancy);

    const categories = ["industry", "product", "service", "support"];
    const byCategory = categories.map((cat) => {
      const catDivisions = divisions.filter((d) => d.category === cat);
      const count = people.filter((p) => catDivisions.some((d) => d.id === p.parent_id)).length;
      return { category: cat, units: catDivisions.length, people: count };
    });

    const roleOrder = ["VP", "Director", "Head", "Manager"];
    const byRole = [
      { role: "Board", label: "Board of Directors", count: boardNodes.length },
      { role: "C-Level", label: "C-Level", count: cSuiteNodes.length },
      ...roleOrder.map((role) => ({
        role,
        label: role === "VP" ? "Vice President" : role,
        count: people.filter((p) => p.role_level === role).length,
      })),
    ];
    const otherCount = people.filter((p) => !roleOrder.includes(p.role_level)).length;
    if (otherCount > 0) byRole.push({ role: "Other", label: "Other", count: otherCount });

    const spanOfControl = cSuiteNodes
      .map((c) => ({
        name: c.name,
        title: c.title,
        count: people.filter((p) => p.c_level_id === c.id).length,
      }))
      .sort((a, b) => b.count - a.count);

    const ROLE_RANK = { VP: 1, Director: 2, Head: 3, Manager: 4 };
    const functionalLeaders = people
      .filter((p) => p.role_level && ROLE_RANK[p.role_level])
      .map((leader) => ({
        name: leader.name,
        title: leader.role_level,
        count: people.filter((p) => (p.collaborators || []).includes(leader.id)).length,
      }))
      .filter((f) => f.count > 0)
      .sort((a, b) => b.count - a.count);

    const SIZE_VALUES = { "N/A": null, "0-15": 15, "15-30": 30, "30-45": 45, "45-60": 60, "60-75": 75, "75-90": 90, "90-105": 105, "105-120": 120, "120-135": 135, "135-150": 150, "150-165": 165, "165-180": 180, "180-195": 195, "195+": 200 };

    const perDivision = divisions
      .map((d) => {
        const actualCount = people.filter((p) => p.parent_id === d.id).length;
        const sizeValue = SIZE_VALUES[d.unit_size] ?? null;
        return {
          name: d.department,
          code: d.department_code,
          count: actualCount,
          vacancies: people.filter((p) => p.parent_id === d.id && p.is_vacancy).length,
          unit_size: d.unit_size || "N/A",
          displayValue: sizeValue !== null ? sizeValue : actualCount,
          displayLabel: sizeValue !== null ? d.unit_size : `${actualCount}`,
        };
      })
      .sort((a, b) => b.displayValue - a.displayValue);

    return {
      totalPeople: people.length,
      totalBoard: boardNodes.length,
      totalCSuite: cSuiteNodes.length,
      totalDivisions: divisions.length,
      totalVacancies: vacancies.length,
      byCategory,
      byRole,
      spanOfControl,
      perDivision,
      maxDivisionCount: Math.max(...perDivision.map((d) => d.displayValue), 1),
      functionalLeaders,
      maxRoleCount: Math.max(...byRole.map((r) => r.count), 1),
      maxSpanCount: Math.max(...spanOfControl.map((s) => s.count), 1),
      maxFunctionalCount: Math.max(...functionalLeaders.map((f) => f.count), 1),
    };
  }, [nodes]);

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-50 flex items-center justify-center p-3 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[97vw] max-h-[92vh] overflow-y-auto ring-1 ring-black/5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h3 className="font-bold text-gray-900">Group Management Statistics</h3>
          <button onClick={onClose} title="Close" className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-5">
          <div className="grid grid-cols-5 gap-3">
            <StatCard icon={Users} label="People" value={stats.totalPeople} color="blue" />
            <StatCard icon={Crown} label="Board" value={stats.totalBoard} color="indigo" />
            <StatCard icon={Briefcase} label="Executives" value={stats.totalCSuite} color="purple" />
            <StatCard icon={Building2} label="Units" value={stats.totalDivisions} color="green" />
            <StatCard icon={UserPlus} label="Vacancies" value={stats.totalVacancies} color="amber" />
          </div>

          <div>
            <p className="text-sm font-bold text-gray-700 mb-2">Group Management by Role</p>
            <div className="space-y-1.5">
              {stats.byRole.map((r) => (
                <div key={r.role} className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 w-40 truncate">{r.label}</span>
                  <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
                    {r.count > 0 && (
                      <div
                        className="h-full bg-blue-500 rounded-full flex items-center justify-end px-2 transition-all"
                        style={{ width: `${Math.max((r.count / stats.maxRoleCount) * 100, 8)}%` }}
                      >
                        <span className="text-[10px] font-bold text-white">{r.count}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="text-sm font-bold text-gray-700 mb-2">Group Management by Unit Type</p>
            <div className="grid grid-cols-4 gap-2">
              {stats.byCategory.map((c) => (
                <div key={c.category} className="border border-gray-200 rounded-lg p-2 text-center">
                  <p className="text-[10px] uppercase tracking-wide text-gray-400">{c.category}</p>
                  <p className="text-lg font-bold text-gray-900">{c.people}</p>
                  <p className="text-[10px] text-gray-500">{c.units} units</p>
                </div>
              ))}
            </div>
          </div>

          {stats.spanOfControl.length > 0 && (
            <div>
              <p className="text-sm font-bold text-gray-700 mb-2">Assigned People by C-Level</p>
              <div className="space-y-1.5">
                {stats.spanOfControl.map((s, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 w-48 truncate">{s.title} · {s.name}</span>
                    <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
                      {s.count > 0 && (
                        <div
                          className="h-full bg-purple-500 rounded-full flex items-center justify-end px-2 transition-all"
                          style={{ width: `${Math.max((s.count / stats.maxSpanCount) * 100, 8)}%` }}
                        >
                          <span className="text-[10px] font-bold text-white">{s.count}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {stats.functionalLeaders.length > 0 && (
            <div>
              <p className="text-sm font-bold text-gray-700 mb-2">Assigned People by Functional Leaders</p>
              <div className="space-y-1.5">
                {stats.functionalLeaders.map((f, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 w-48 truncate">{f.title} · {f.name}</span>
                    <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
                      {f.count > 0 && (
                        <div
                          className="h-full bg-purple-500 rounded-full flex items-center justify-end px-2 transition-all"
                          style={{ width: `${Math.max((f.count / stats.maxFunctionalCount) * 100, 8)}%` }}
                        >
                          <span className="text-[10px] font-bold text-white">{f.count}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="border-t border-gray-200 pt-4 mt-2">
            <p className="text-sm font-bold text-gray-700 mb-2">Group Total People per Unit</p>
            <div className="space-y-1.5">
              {stats.perDivision.map((d, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 w-52 truncate">
                    {d.name}{d.code ? ` [${d.code}]` : ""}
                  </span>
                  <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
                    {d.displayValue > 0 && (
                      <div
                        className="h-full bg-green-500 rounded-full flex items-center justify-end px-2 transition-all overflow-visible"
                        style={{ width: `${Math.max((d.displayValue / stats.maxDivisionCount) * 100, 8)}%` }}
                      >
                        <span className="text-[10px] font-bold text-white whitespace-nowrap flex-shrink-0">{d.displayLabel}</span>
                      </div>
                    )}
                  </div>
                  {d.vacancies > 0 && (
                    <span className="text-[10px] text-amber-600 font-medium">{d.vacancies} open</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  const colors = {
    blue: "bg-blue-50 text-blue-600",
    indigo: "bg-indigo-50 text-indigo-600",
    purple: "bg-purple-50 text-purple-600",
    green: "bg-green-50 text-green-600",
    amber: "bg-amber-50 text-amber-600",
  };
  return (
    <div className="border border-gray-200 rounded-lg p-3 text-center">
      <div className={`w-8 h-8 rounded-full mx-auto mb-1 flex items-center justify-center ${colors[color]}`}>
        <Icon className="w-4 h-4" />
      </div>
      <p className="text-xl font-bold text-gray-900">{value}</p>
      <p className="text-[10px] uppercase tracking-wide text-gray-400">{label}</p>
    </div>
  );
}